// ============================================
// Clinical Capture Strip — horizontal scrollable
// pill bar showing clinical signals extracted
// from patient conversation (Section 8.2).
// ============================================

import { T, F } from '../../quality/tokens';
import type { CapturedSignal, CaptureCategory } from '../../../types/fashionPlate';

// Category color mapping (Section 8.2)
const CATEGORY_COLORS: Record<CaptureCategory, { color: string; bg: string }> = {
  symptom:    { color: '#2563EB', bg: 'rgba(37,99,235,0.08)' },
  medication: { color: '#16A34A', bg: 'rgba(22,163,74,0.08)' },
  life_event: { color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  context:    { color: '#D97706', bg: 'rgba(217,119,6,0.08)' },
  history:    { color: '#64748B', bg: 'rgba(100,116,139,0.08)' },
};

interface CaptureStripProps {
  signals: CapturedSignal[];
  onSignalClick?: (signal: CapturedSignal) => void;
}

export function CaptureStrip({ signals, onSignalClick }: CaptureStripProps) {
  if (signals.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 16px',
      overflowX: 'auto',
      overflowY: 'hidden',
      borderBottom: `1px solid ${T.borderSoft}`,
      background: T.bgSoft,
    }}>
      {/* CAPTURED label */}
      <span style={{
        fontSize: 8,
        fontFamily: F.mono,
        fontWeight: 600,
        color: T.textGhost,
        textTransform: 'uppercase',
        letterSpacing: 2,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}>
        CAPTURED
      </span>

      {/* Signal pills */}
      {signals.map((signal) => {
        const colors = CATEGORY_COLORS[signal.category];

        return (
          <button
            key={signal.id}
            onClick={onSignalClick ? () => onSignalClick(signal) : undefined}
            style={{
              fontSize: 10,
              fontFamily: F.mono,
              fontWeight: 600,
              padding: '3px 8px',
              borderRadius: T.radius.full,
              background: colors.bg,
              color: colors.color,
              border: 'none',
              cursor: onSignalClick ? 'pointer' : 'default',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              letterSpacing: 0.5,
              lineHeight: 1.4,
              transition: 'opacity 0.15s ease',
            }}
          >
            {signal.term}
          </button>
        );
      })}
    </div>
  );
}
